import { Stack } from '@chakra-ui/react';
import { createFieldHelper, FilterForm, useHookForm } from '@cks-internal/react-base';
import { ActivityInformationTravellerDisplay } from '../displays/activityInformationTravellerDisplay';
import { TravellerSearchForm } from '../utils/types';
import { useActivityInformationTravellerSearch } from '../api/useActivityInformationTravellerSearchQuery';
import { useState } from 'react';
/**
 * TravellerSearch component renders a search form for travelers and displays matching results.
 *
 * Responsibilities:
 * - Initializes a React Hook Form instance for `TravellerSearchForm`.
 * - Executes a search mutation via `useActivityInformationTravellerSearch` when the form is submitted.
 * - Stores the resulting traveler IDs in component state.
 * - Defines form fields for text, first name, last name, phone number, and email.
 * - Renders a `FilterForm` with inline search controls.
 * - On successful search, renders `ActivityInformationTravellerDisplay` with the returned IDs.
 *
 * @component
 * @returns {JSX.Element} A stack containing the search form and, upon search, the traveler display.
 */

type TravellerSearchProps = {
  companyId: number | undefined;
};

export const TravellerSearch = (props: TravellerSearchProps) => {
  const { formMethods } = useHookForm<Partial<TravellerSearchForm>>({});
  const travellerQuery = useActivityInformationTravellerSearch();
  const [travellerIds, setTravellerIds] = useState<number[]>();

  const helper = createFieldHelper<Partial<TravellerSearchForm>>(formMethods);
  const fields = [
    helper.textInput('Text'),
    helper.textInput('FirstName'),
    helper.textInput('LastName'),
    helper.textInput('PhoneNumber'),
    helper.textInput('Email')
  ];

  const onSearch = async (model: Partial<TravellerSearchForm>) => {
    const response = await travellerQuery.mutateAsync({ model });
    setTravellerIds(response.data);
  };

  return (
    <Stack>
      <FilterForm formMethods={formMethods} onSubmit={onSearch} formFields={fields} inlineButtons></FilterForm>
      {travellerIds && (
        <ActivityInformationTravellerDisplay
          companyId={props.companyId}
          requestedById={undefined}
          approvedBookerId={undefined}
          travellerIds={travellerIds}
          displayCompany
        />
      )}
    </Stack>
  );
};
